/**
 * Rules for saving an unfinished product from the New Product wizard.
 *
 * A draft is a partially-filled product the user means to come back to, so it
 * must persist from any step without demanding fields belonging to steps they
 * have not opened yet. The API still requires a name, a numeric price/cost and
 * a valid tax slab, so anything untouched is defaulted rather than bounced back
 * at the user as a validation error.
 */

/** Tax slabs the API accepts. A draft with no slab chosen defaults to 0. */
const VALID_TAX_RATES = [0, 5, 12, 18, 28];

/**
 * A draft only needs enough to identify the record later. Everything else is
 * defaulted by `applyDraftDefaults`.
 */
export function validateDraft(fields: { name: string }): Record<string, string> {
    const errs: Record<string, string> = {};
    const name = fields.name.trim();
    if (!name || name.length < 2) {
        errs.name = 'Give the draft a name of at least 2 characters so you can find it later';
    }
    return errs;
}

/** `Number('')` is NaN, which the API rejects — coerce blanks to a default. */
export function numberOrDefault(value: string | number | undefined, fallback: number): number {
    if (value === '' || value === undefined || value === null) return fallback;
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

/** A tax rate the API will accept, falling back to 0 for an unvisited step. */
export function taxRateOrDefault(value: string | number | undefined): number {
    const n = numberOrDefault(value, 0);
    return VALID_TAX_RATES.includes(n) ? n : 0;
}

/**
 * Fill the gaps a draft is allowed to leave. Only touches fields the user has
 * not supplied — a value they did type is never overwritten.
 */
export interface DraftDefaults {
    price: number;
    cost: number;
    gst_rate: number;
    purchase_gst_rate: number;
    stock_quantity: number;
    /** null = Min Stock left empty: no minimum (C04). */
    low_stock_threshold: number | null;
    is_active: false;
    is_draft: true;
}

export function applyDraftDefaults<T extends Record<string, unknown>>(
    payload: T,
): T & DraftDefaults {
    return {
        ...payload,
        price: numberOrDefault(payload.price as string | number, 0),
        cost: numberOrDefault(payload.cost as string | number, 0),
        gst_rate: taxRateOrDefault(payload.gst_rate as string | number),
        purchase_gst_rate: taxRateOrDefault(payload.purchase_gst_rate as string | number),
        stock_quantity: numberOrDefault(payload.stock_quantity as string | number, 0),
        // An emptied Min Stock stays empty (C04); only a missing one becomes 10.
        low_stock_threshold: payload.low_stock_threshold === null
            ? null
            : numberOrDefault(payload.low_stock_threshold as string | number, 10),
        // A draft is never sellable, and is flagged so the list can tell it
        // apart from a completed product that was deliberately deactivated.
        is_active: false,
        is_draft: true,
    };
}
